import { loadContentData } from "@src/packages/content/loader";
import { getBlocksByKind } from "@src/packages/content";
import type { DataPointBody } from "@src/packages/content/types";
import { normalizeBiomarkerName } from "./biomarker-identity";

loadContentData();

/**
 * The units labs report a marker in, the conventional unit first and then
 * the SI unit many labs use instead. A value field offers these as the
 * marker's unit choice, so a result can be entered in the unit on its
 * report. A test result stores its unit as text, so either can be saved.
 *
 * `names` are the English names a stored result carries; the content data
 * point adds its own title and aliases in the active language.
 */
const MARKER_UNITS: {
  dataPointId: string;
  names: string[];
  units: readonly string[];
}[] = [
  {
    dataPointId: "dp-amh-level",
    names: ["AMH", "Anti-Mullerian hormone"],
    units: ["ng/mL", "pmol/L"],
  },
  {
    dataPointId: "dp-estradiol-level",
    names: ["Estradiol", "E2", "Oestradiol"],
    units: ["pg/mL", "pmol/L"],
  },
  {
    dataPointId: "dp-lh-level",
    names: ["LH", "Luteinizing hormone"],
    units: ["mIU/mL", "IU/L"],
  },
  {
    dataPointId: "dp-fsh-level",
    names: ["FSH", "Follicle-stimulating hormone"],
    units: ["mIU/mL", "IU/L"],
  },
  {
    dataPointId: "dp-progesterone-level",
    names: ["Progesterone", "P4"],
    units: ["ng/mL", "nmol/L"],
  },
  {
    dataPointId: "dp-tsh-level",
    names: ["TSH"],
    units: ["mIU/L", "µIU/mL"],
  },
  {
    dataPointId: "dp-free-t4-level",
    names: ["Free T4", "FT4"],
    units: ["ng/dL", "pmol/L"],
  },
  {
    dataPointId: "dp-free-t3-level",
    names: ["Free T3", "FT3"],
    units: ["pg/mL", "pmol/L"],
  },
  {
    dataPointId: "dp-vitamin-d-level",
    names: ["Vitamin D"],
    units: ["ng/mL", "nmol/L"],
  },
  {
    dataPointId: "dp-beta-hcg-level",
    names: ["Beta hCG", "hCG"],
    units: ["mIU/mL", "IU/L"],
  },
  {
    dataPointId: "dp-prolactin-level",
    names: ["Prolactin"],
    units: ["ng/mL", "mIU/L"],
  },
  {
    dataPointId: "dp-testosterone-level",
    names: ["Testosterone"],
    units: ["ng/dL", "nmol/L"],
  },
  {
    dataPointId: "dp-dhea-s-level",
    names: ["DHEA-S"],
    units: ["µg/dL", "µmol/L"],
  },
  {
    dataPointId: "dp-fasting-insulin",
    names: ["Fasting insulin", "Insulin"],
    units: ["µIU/mL", "pmol/L"],
  },
  {
    dataPointId: "dp-hba1c",
    names: ["HbA1c"],
    units: ["%", "mmol/mol"],
  },
  {
    dataPointId: "dp-ferritin",
    names: ["Ferritin"],
    units: ["ng/mL", "µg/L"],
  },
  {
    dataPointId: "dp-apob",
    names: ["ApoB"],
    units: ["mg/dL", "g/L"],
  },
];

/** The names a data point goes by in the active language. */
function dataPointNames(dataPointId: string): string[] {
  const block = getBlocksByKind("data-point").find(
    (candidate) => candidate.id === dataPointId,
  );
  if (!block) return [];
  return [block.title, ...((block.body as DataPointBody).aliases ?? [])];
}

/**
 * The units a marker is reported in, or an empty list for a marker this
 * catalogue does not know. Matches the whole name, so a name still being
 * typed ("A") does not settle on a marker that merely contains it.
 */
export function getMarkerUnits(markerName: string): readonly string[] {
  const query = normalizeBiomarkerName(markerName);
  if (!query) return [];
  const entry = MARKER_UNITS.find((candidate) =>
    [...candidate.names, ...dataPointNames(candidate.dataPointId)].some(
      (name) => normalizeBiomarkerName(name) === query,
    ),
  );
  return entry?.units ?? [];
}

/** Two spellings of one unit ("ng/ml" and "ng/mL", "uIU/mL" and "µIU/mL"). */
export function isSameUnit(left: string, right: string): boolean {
  const normalize = (unit: string) =>
    unit.trim().toLowerCase().replace(/[µμ]/g, "u");
  return normalize(left) === normalize(right);
}
